// Arbeidsseddel for ett tekstiltrykk (vinnsel 30.10): kunden har den med i kassa, så den viser bare det
// som trengs for å ta betalt: trykknummer, dato, kunde, telefon, vare og linjene med pris. Det som
// er betalt («Tøyet er betalt» = produktene, «Trykket er betalt» = arbeidet) strykes ut, og «Å betale»
// er resten (src/trykk/linjer.js). Interne notater («Hva skal trykkes?», utført arbeid) er ikke med.
// Brukes av «Skriv ut arbeidsseddel» i ServiceDetailsClub. Alt fra brukere og databasen escapes (#30).
import { escapeHtml } from '../utils/format';
import { linjeTall } from '../tilbud/kalkyle';
import { linjeBetalt, betalingssum } from './linjer';

const e = (v) => escapeHtml(v ?? '');
const kr = (n) => `${new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 }).format(Number(n) || 0)} kr`;
const verdi = (v) => (v ? e(v) : '<span class="tom">–</span>');
const dato = (d) => {
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '' : x.toLocaleDateString('nb-NO', { day: 'numeric', month: 'numeric', year: 'numeric' });
};

// trykk: lagret trykk (serviceid, registrertDato, toyBetalt, trykkBetalt); felt: skjemaet (varen);
// linjer: prislinjene (src/trykk/linjer.js). Skrives bare ut etter lagring.
export function arbeidsseddelHtml({ trykk, kunde, felt, linjer = [], utskrevet = new Date() }) {
  const betaling = { toyBetalt: !!trykk.toyBetalt, trykkBetalt: !!trykk.trykkBetalt };
  const kundeNavn = [kunde?.firstName, kunde?.lastName].filter(Boolean).join(' ');
  const vare = [felt.Varemerke, felt.Produkt, felt.Størrelse && `str. ${felt.Størrelse}`, felt.Farge].filter(Boolean).join(', ');
  const sum = betalingssum(linjer, betaling);

  const linjeRader = linjer.map((l) => {
    const t = linjeTall(l);
    const betalt = linjeBetalt(l, betaling);
    const rabatt = t.rabattKr > 0 ? `${kr(t.rabattKr)} (${e(String(Math.round((t.rabattKr / (t.subtotal || 1)) * 1000) / 10).replace('.', ','))} %)` : '';
    return `<tr${betalt ? ' class="betalt"' : ''}>
      <td><span class="tekst">${e(l.artikkel)}</span>${betalt ? ' <span class="merke">Betalt</span>' : ''}</td>
      <td class="tall">${Math.max(1, Math.round(Number(l.antall) || 1))}</td>
      <td class="tall">${kr(l.pris)}</td>
      <td class="tall">${rabatt}</td>
      <td class="tall">${kr(t.total)}</td>
    </tr>`;
  }).join('');

  return `<!doctype html>
<html lang="no">
<head>
<meta charset="utf-8">
<title>Arbeidsseddel – trykk ${e(trykk.serviceid)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 12pt; margin: 0; }
  header { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 2px solid #111; padding-bottom: 6pt; margin-bottom: 10pt; }
  h1 { font-size: 22pt; margin: 0; }
  .dato { font-size: 12pt; }
  table { width: 100%; border-collapse: collapse; }
  th, td { text-align: left; vertical-align: top; padding: 4pt 6pt; }
  .info { margin-bottom: 12pt; }
  .info th { width: 18%; font-weight: normal; color: #333; }
  .info th, .info td { border-bottom: 1px solid #ddd; }
  .info td { font-weight: bold; }
  .linjer th { border-bottom: 1.5px solid #111; font-size: 10pt; }
  .linjer td { border-bottom: 1px solid #ddd; }
  .tall { text-align: right; white-space: nowrap; }
  .betalt td, .betalt .tekst { text-decoration: line-through; color: #555; }
  .betalt td:first-child { text-decoration: none; }
  .merke { font-size: 9pt; font-weight: bold; border: 1px solid #111; padding: 0 4pt; text-decoration: none; color: #111; }
  .sum td { font-weight: bold; }
  .total td { border-top: 2px solid #111; }
  .abetale td { font-size: 16pt; font-weight: bold; border-top: 2px solid #111; }
  .tom { color: #888; font-weight: normal; }
</style>
</head>
<body>
  <header>
    <h1>Trykk nr. ${e(trykk.serviceid)}</h1>
    <div class="dato">${e(dato(trykk.registrertDato || utskrevet))}</div>
  </header>

  <table class="info">
    <tr><th>Kunde</th><td>${verdi(kundeNavn)}</td></tr>
    <tr><th>Telefon</th><td>${verdi(kunde?.phoneNumber)}</td></tr>
    <tr><th>Vare</th><td>${verdi(vare)}</td></tr>
  </table>

  ${linjer.length ? `<table class="linjer">
    <tr><th>Linje</th><th class="tall">Antall</th><th class="tall">Pris</th><th class="tall">Rabatt</th><th class="tall">Sum</th></tr>
    ${linjeRader}
    <tr class="sum total"><td colspan="4">Totalt</td><td class="tall">${kr(sum.total)}</td></tr>
    ${sum.betalt > 0 ? `<tr class="sum"><td colspan="4">Betalt</td><td class="tall">− ${kr(sum.betalt)}</td></tr>` : ''}
    <tr class="abetale"><td colspan="4">Å betale</td><td class="tall">${kr(sum.aBetale)}</td></tr>
  </table>` : '<p class="tom">Ingen arbeid eller produkter registrert.</p>'}
</body>
</html>`;
}
